import React, { useState, useEffect } from 'react';
import { Popconfirm, Tag, Table, Form, Input, Button, Space, Card, Col, message, Tooltip, Modal, Select } from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined, KeyOutlined, SafetyCertificateOutlined, ShopOutlined, CheckCircleOutlined, StopOutlined, CheckOutlined, SafetyOutlined, CheckSquareOutlined, StarOutlined } from '@ant-design/icons';
import FilterPanel from '../../components/common/FilterPanel';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as accountService from '../../services/account/accountService';
import AccountCreateForm from './AccountCreateForm';
import AccountEditForm from './AccountEditForm';
import AccountPasswordForm from './AccountPasswordForm';
import AccountRoleAuthModal from './AccountRoleAuthModal';
import AccountApprovalDrawer from './AccountApprovalDrawer';
import AccountStoreAuthDrawer from './AccountStoreAuthDrawer';
import './index.css';

const AccountManagement = () => {
    const [form] = Form.useForm();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [dataSource, setDataSource] = useState([]);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [createFormVisible, setCreateFormVisible] = useState(false);
    const [editFormVisible, setEditFormVisible] = useState(false);
    const [passwordFormVisible, setPasswordFormVisible] = useState(false);
    const [roleAuthVisible, setRoleAuthVisible] = useState(false);
    const [rolesModalVisible, setRolesModalVisible] = useState(false);
    const [selectedRoles, setSelectedRoles] = useState([]);
    const [currentAccount, setCurrentAccount] = useState(null);
    const [approvalDrawerVisible, setApprovalDrawerVisible] = useState(false);
    const [storeAuthDrawerVisible, setStoreAuthDrawerVisible] = useState(false);
    const [proficiencyModalVisible, setProficiencyModalVisible] = useState(false);
    const [proficiencyLevel, setProficiencyLevel] = useState(null);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
        showSizeChanger: true,
        showQuickJumper: true,
        showTotal: (total, range) => `${t('common.operation')} ${range[0]}-${range[1]} / ${t('common.total')} ${total}`
    });

    const columns = [
        {
            title: t('account.employeeId'),
            dataIndex: 'loginId',
            key: 'loginId',
            width: 140,
            fixed: 'left',
            render: (text, record) => {
                const displayText = text ? text.charAt(0).toUpperCase() + text.slice(1) : text;
                return <a onClick={() => handleEdit(record)}>{displayText}</a>;
            },
        },
        {
            title: t('account.name'),
            dataIndex: 'name',
            key: 'name',
            width: 120,
        },
        {
            title: t('account.role'),
            dataIndex: 'roleNameArr',
            key: 'roleNameArr',
            width: 200,
            render: (roles) => {
                if (!roles || roles.length === 0) {
                    return null;
                }
                
                // 如果角色数量小于等于5个，直接显示所有角色
                if (roles.length <= 5) {
                    return (
                        <>
                            {roles.map(role => (
                                <Tag color="blue" key={role}>
                                    {role}
                                </Tag>
                            ))}
                        </>
                    );
                }
                
                // 如果角色数量超过5个，显示前5个角色和一个"更多"按钮
                const displayedRoles = roles.slice(0, 5);
                const remainingRoles = roles.slice(5);
                
                const showAllRoles = () => {
                    setSelectedRoles(roles);
                    setRolesModalVisible(true);
                };
                
                return (
                    <>
                        {displayedRoles.map(role => (
                            <Tag color="blue" key={role}>
                                {role}
                            </Tag>
                        ))}
                        <Button 
                            type="link" 
                            size="small" 
                            onClick={showAllRoles}
                            style={{ padding: 0 }}
                        >
                            +{remainingRoles.length} {t('common.more')}
                        </Button>
                    </>
                );
            },
        },
        {
            title: t('account.status'),
            dataIndex: 'accountStatus',
            key: 'accountStatus',
            width: 120,
            render: (status) => {
                if (status === 3) {
                    return <Tag color="orange">{t('account.approval.pending')}</Tag>;
                }
                return status === 1
                    ? <Tag color="green">{t('account.statusEnabled')}</Tag>
                    : <Tag color="default">{t('account.statusDisabled')}</Tag>;
            },
        },
        {
            title: t('account.contactPhone'),
            dataIndex: 'mobile',
            key: 'mobile',
            width: 130,
        },
        {
            title: t('account.employeeType'),
            dataIndex: 'sourceCode',
            key: 'sourceCode',
            width: 120,
            render: (val) => val === 1 ? 'Formal' : val === 2 ? 'Dummy' : '-',
        },
        {
            title: t('account.proficiency'),
            dataIndex: 'proficiency',
            key: 'proficiency',
            width: 100,
        },
        {
            title: t('account.expiryDate'),
            dataIndex: 'expireTime',
            key: 'expireTime',
            width: 120,
            render: (val) => val || '-',
        },
        {
            title: t('account.rejectReason'),
            dataIndex: 'rejectReason',
            key: 'rejectReason',
            width: 150,
            render: (val) => val || '-',
        },
        {
            title: t('account.createdAt'),
            dataIndex: 'gmtCreateStr',
            key: 'gmtCreateStr',
            width: 180,
        },
        {
            title: t('account.actions'),
            key: 'actions',
            width: 220,
            fixed: 'right',
            render: (record) => (
                <Space size="small">
                    {record.accountStatus === 3 && (
                        <Tooltip title={t('account.approval.approve')}>
                            <Button 
                                type="link" 
                                size="small" 
                                icon={<CheckSquareOutlined />}
                                onClick={() => handleApproval(record)}
                            />
                        </Tooltip>
                    )}
                    <Tooltip title={t('account.edit')}>
                        <Button 
                            type="link" 
                            size="small" 
                            icon={<EditOutlined />}
                            onClick={() => handleEdit(record)}
                        />
                    </Tooltip>
                    <Tooltip title={t('account.editPassword')}>
                        <Button 
                            type="link" 
                            size="small" 
                            icon={<KeyOutlined />}
                            onClick={() => handleChangePassword(record)}
                        />
                    </Tooltip>
                    <Tooltip title={t('account.roleAuthorization')}>
                        <Button 
                            type="link" 
                            size="small" 
                            icon={<SafetyCertificateOutlined />}
                            onClick={() => handleRoleAuth(record)}
                        />
                    </Tooltip>
                    <Tooltip title={t('account.storePermissionAuthorization')}>
                        <Button 
                            type="link" 
                            size="small" 
                            icon={<ShopOutlined />}
                            onClick={() => handleStoreAuth(record)}
                        />
                    </Tooltip>
                    <Tooltip title={t('account.updateProficiency')}>
                        <Button 
                            type="link" 
                            size="small" 
                            icon={<StarOutlined />}
                            onClick={() => handleUpdateProficiency(record)}
                        />
                    </Tooltip>
                    {record.accountStatus === 2 && (
                        <Tooltip title={t('common.delete')}>
                            <Popconfirm
                                title={t('account.deleteConfirm')}
                                onConfirm={() => handleDelete(record.loginId)}
                                okText={t('common.confirm')}
                                cancelText={t('common.cancel')}
                            >
                                <Button
                                    type="link"
                                    size="small"
                                    danger
                                    icon={<DeleteOutlined />}
                                />
                            </Popconfirm>
                        </Tooltip>
                    )}
                </Space>
            ),
        },
    ];

    const fetchData = async (params = {}) => {
        setLoading(true);
        try {
            const searchParams = {
                ...form.getFieldsValue(),
                currentPage: pagination.current,
                pageSize: pagination.pageSize,
                ...params,
            };

            const response = await accountService.pageAccounts(searchParams);

            if (response.success) {
                const data = response.data || {};
                setDataSource(data.tableData);
                setPagination(prev => ({
                    ...prev,
                    current: data.paging?.currentPage || 1,
                    total: data.paging?.totalCount || 0
                }));
            } else {
                message.error(response.errorMessage || t('account.queryFailed'));
            }
        } catch (error) {
            console.error('Query failed:', error);
            message.error(t('account.queryFailedRetry'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleSearch = (values) => {
        form.setFieldsValue(values);
        setPagination(prev => ({ ...prev, current: 1 }));
        fetchData({ currentPage: 1 });
    };

    const handleReset = () => {
        form.resetFields();
        setPagination(prev => ({ ...prev, current: 1 }));
        fetchData({ currentPage: 1 });
    };

    const handleTableChange = (paginationConfig) => {
        const newPagination = {
            ...pagination,
            current: paginationConfig.current,
            pageSize: paginationConfig.pageSize
        };
        setPagination(newPagination);
        fetchData({
            currentPage: paginationConfig.current,
            pageSize: paginationConfig.pageSize
        });
    };

    const handleAdd = () => {
        setCreateFormVisible(true);
    };

    const handleEdit = (account) => {
        setCurrentAccount(account);
        setEditFormVisible(true);
    };

    const handleChangePassword = (account) => {
        setCurrentAccount(account);
        setPasswordFormVisible(true);
    };

    const handleRoleAuth = (account) => {
        setCurrentAccount(account);
        setRoleAuthVisible(true);
    };

    const handleStoreAuth = (account) => {
        setCurrentAccount(account);
        setStoreAuthDrawerVisible(true);
    };

    const handleStoreAuthCancel = () => {
        setStoreAuthDrawerVisible(false);
        setCurrentAccount(null);
    };

    const handleStoreAuthSuccess = () => {
        setStoreAuthDrawerVisible(false);
        setCurrentAccount(null);
        fetchData();
    };

    const handleApproval = (account) => {
        setCurrentAccount(account);
        setApprovalDrawerVisible(true);
    };

    const handleUpdateProficiency = (account) => {
        setCurrentAccount(account);
        setProficiencyLevel(account.proficiency || null);
        setProficiencyModalVisible(true);
    };

    const handleProficiencyConfirm = async () => {
        if (!proficiencyLevel) {
            message.warning(t('account.selectProficiencyLevel'));
            return;
        }
        try {
            const response = await accountService.updateProficiency(currentAccount.loginId, proficiencyLevel);
            if (response.success) {
                message.success(t('account.updateProficiencySuccess'));
                setProficiencyModalVisible(false);
                setCurrentAccount(null);
                fetchData();
            } else {
                message.error(response.errorMessage || t('account.updateProficiencyFailed'));
            }
        } catch (error) {
            console.error('Update proficiency failed:', error);
            message.error(t('account.updateProficiencyFailed'));
        }
    };

    const handleApprovalCancel = () => {
        setApprovalDrawerVisible(false);
        setCurrentAccount(null);
    };

    const handleApprovalSuccess = () => {
        setApprovalDrawerVisible(false);
        setCurrentAccount(null);
        fetchData();
    };

    const handleDelete = async (loginId) => {
        try {
            const response = await accountService.deleteAccount(loginId);
            if (response.success) {
                message.success(t('account.deleteSuccess'));
                fetchData();
            } else {
                message.error(response.errorMessage || t('account.deleteFailed'));
            }
        } catch (error) {
            console.error('Delete failed:', error);
            message.error(t('account.deleteFailedRetry'));
        }
    };

    const handleCreateFormCancel = () => {
        setCreateFormVisible(false);
    };

    const handleCreateFormSuccess = () => {
        setCreateFormVisible(false);
        fetchData();
    };

    const handleEditFormCancel = () => {
        setEditFormVisible(false);
        setCurrentAccount(null);
    };

    const handleEditFormSuccess = () => {
        setEditFormVisible(false);
        setCurrentAccount(null);
        fetchData();
    };

    const handlePasswordFormCancel = () => {
        setPasswordFormVisible(false);
        setCurrentAccount(null);
    };

    const handlePasswordFormSuccess = () => {
        setPasswordFormVisible(false);
        setCurrentAccount(null);
    };

    const handleRoleAuthCancel = () => {
        setRoleAuthVisible(false);
        setCurrentAccount(null);
    };

    const handleRoleAuthSuccess = () => {
        setRoleAuthVisible(false);
        setCurrentAccount(null);
        fetchData(); // 重新加载数据以更新角色信息
    };

    const handleEnableAccounts = async () => {
        if (selectedRowKeys.length === 0) {
            message.warning(t('account.selectAtLeastOne'));
            return;
        }
        
        Modal.confirm({
            title: t('account.enableConfirmTitle'),
            content: t('account.enableConfirmContent', { count: selectedRowKeys.length }),
            onOk: async () => {
                try {
                    const response = await accountService.enableAccounts(selectedRowKeys);
                    if (response.success) {
                        message.success(t('account.enableSuccess'));
                        setSelectedRowKeys([]);
                        fetchData();
                    } else {
                        message.error(response.errorMessage || t('account.enableFailed'));
                    }
                } catch (error) {
                    console.error('Enable accounts failed:', error);
                    message.error(t('account.enableFailed'));
                }
            },
        });
    };

    const handleDisableAccounts = async () => {
        if (selectedRowKeys.length === 0) {
            message.warning(t('account.selectAtLeastOne'));
            return;
        }
        
        Modal.confirm({
            title: t('account.disableConfirmTitle'),
            content: t('account.disableConfirmContent', { count: selectedRowKeys.length }),
            onOk: async () => {
                try {
                    const response = await accountService.disableAccounts(selectedRowKeys);
                    if (response.success) {
                        message.success(t('account.disableSuccess'));
                        setSelectedRowKeys([]);
                        fetchData();
                    } else {
                        message.error(response.errorMessage || t('account.disableFailed'));
                    }
                } catch (error) {
                    console.error('Disable accounts failed:', error);
                    message.error(t('account.disableFailed'));
                }
            },
        });
    };

    const rowSelection = {
        selectedRowKeys,
        onChange: (newSelectedRowKeys) => {
            setSelectedRowKeys(newSelectedRowKeys);
        },
        getCheckboxProps: (record) => ({
            disabled: record.accountStatus === 3, // 待审批的不能选择
        }),
    };

    return (
        <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <FilterPanel
                form={form}
                onFilter={handleSearch}
                onReset={handleReset}
                title={t('account.title')}
                initialValues={{}}
            >
                <Col span={6}>
                    <Form.Item name="loginId" label={t('account.employeeId')}>
                        <Input placeholder={t('account.employeeId') + t('common.search')} />
                    </Form.Item>
                </Col>
                <Col span={6}>
                    <Form.Item name="nameLike" label={t('account.name')}>
                        <Input placeholder={t('account.name') + t('common.search')} />
                    </Form.Item>
                </Col>
                <Col span={6}>
                    <Form.Item name="accountStatus" label={t('account.status')}>
                        <Select
                            placeholder={t('common.pleaseSelect')}
                            allowClear
                            options={[
                                { value: 1, label: t('account.statusEnabled') },
                                { value: 0, label: t('account.statusDisabled') },
                                { value: 3, label: t('account.approval.pending') },
                            ]}
                        />
                    </Form.Item>
                </Col>
                <Col span={6}>
                    <Form.Item name="sourceCode" label={t('account.employeeType')}>
                        <Select
                            placeholder={t('common.pleaseSelect')}
                            allowClear
                            options={[
                                { value: 1, label: 'Formal' },
                                { value: 2, label: 'Dummy' },
                            ]}
                        />
                    </Form.Item>
                </Col>
            </FilterPanel>

            <Card className="account-table" style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }} bodyStyle={{ flex: 1, overflow: 'hidden', padding: '0 24px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
                    <Space>
                        <Button 
                            icon={<CheckOutlined />}
                            onClick={handleEnableAccounts}
                            disabled={selectedRowKeys.length === 0}
                        >
                            {t('account.enable')}
                        </Button>
                        <Button 
                            icon={<StopOutlined />}
                            onClick={handleDisableAccounts}
                            disabled={selectedRowKeys.length === 0}
                        >
                            {t('account.disable')}
                        </Button>
                    </Space>
                    <Button 
                        type="primary" 
                        icon={<PlusOutlined />}
                        onClick={handleAdd}
                    >
                        {t('account.new')}
                    </Button>
                </div>
                <Table
                    columns={columns}
                    dataSource={dataSource}
                    loading={loading}
                    pagination={pagination}
                    onChange={handleTableChange}
                    rowKey="id"
                    scroll={{ x: 2000, y: 'calc(100vh - 420px)' }}
                    rowSelection={rowSelection}
                />
            </Card>

            <AccountCreateForm
                visible={createFormVisible}
                onCancel={handleCreateFormCancel}
                onSuccess={handleCreateFormSuccess}
            />

            <AccountEditForm
                visible={editFormVisible}
                account={currentAccount}
                onCancel={handleEditFormCancel}
                onSuccess={handleEditFormSuccess}
            />

            <AccountPasswordForm
                visible={passwordFormVisible}
                account={currentAccount}
                onCancel={handlePasswordFormCancel}
                onSuccess={handlePasswordFormSuccess}
            />

            <AccountRoleAuthModal
                visible={roleAuthVisible}
                account={currentAccount}
                onCancel={handleRoleAuthCancel}
                onSuccess={handleRoleAuthSuccess}
            />

            <AccountApprovalDrawer
                visible={approvalDrawerVisible}
                account={currentAccount}
                onCancel={handleApprovalCancel}
                onSuccess={handleApprovalSuccess}
            />

            <AccountStoreAuthDrawer
                visible={storeAuthDrawerVisible}
                account={currentAccount}
                onCancel={handleStoreAuthCancel}
                onSuccess={handleStoreAuthSuccess}
            />
            
            <Modal
                title={t('account.allRoles')}
                open={rolesModalVisible}
                onCancel={() => setRolesModalVisible(false)}
                footer={null}
                width={600}
            >
                <div style={{ maxHeight: 400, overflowY: 'auto' }}>
                    {selectedRoles.map((role, index) => (
                        <div 
                            key={index} 
                            style={{ 
                                padding: '8px 12px', 
                                marginBottom: 8, 
                                backgroundColor: '#f5f5f5',
                                borderRadius: 4 
                            }}
                        >
                            {role}
                        </div>
                    ))}
                </div>
            </Modal>

            <Modal
                title={t('account.updateProficiency')}
                open={proficiencyModalVisible}
                onCancel={() => { setProficiencyModalVisible(false); setCurrentAccount(null); }}
                onOk={handleProficiencyConfirm}
                okText={t('common.confirm')}
                cancelText={t('common.cancel')}
            >
                <div style={{ marginBottom: 16 }}>
                    <span>{t('account.employeeId')}: </span>
                    <strong>{currentAccount?.loginId}</strong>
                </div>
                <div>
                    <span>{t('account.proficiency')}: </span>
                    <Select
                        style={{ width: 200 }}
                        value={proficiencyLevel}
                        onChange={(val) => setProficiencyLevel(val)}
                        placeholder={t('common.pleaseSelect')}
                        options={[
                            { value: 'L1', label: 'L1' },
                            { value: 'L2', label: 'L2' },
                            { value: 'L3', label: 'L3' },
                        ]}
                    />
                </div>
            </Modal>
        </div>
    );
};

export default AccountManagement;